import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MinusCircle, AlertTriangle, XCircle, Filter, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { FALTAS } from "@/data/faltasDgt";
import { useSeo, SITE_URL } from "@/lib/seo";

const PERMITS = [
  { id: "AM",  label: "AM (ciclomotor)" },
  { id: "AML", label: "AM limitado" },
  { id: "A1",  label: "A1 (moto ≤ 125cc)" },
  { id: "A2",  label: "A2 (moto)" },
  { id: "B",   label: "B (coche)" },
  { id: "B96", label: "B96 (coche con remolque)" },
  { id: "BE",  label: "B+E (coche con remolque pesado)" },
  { id: "C",   label: "C (camión)" },
  { id: "CE",  label: "C+E (tráiler)" },
  { id: "D",   label: "D (autobús)" },
];

const NO_DESTREZA = new Set(["B"]);

const TESTS = [
  { id: "D", label: "Destreza (circuito cerrado)", baremo: "1 eliminatoria · 2 deficientes · 1D+2L · 4 leves" },
  { id: "C", label: "Circulación (vías abiertas)",  baremo: "1 eliminatoria · 2 deficientes · 1D+5L · 10 leves" },
];

const GRAVEDAD = {
  L: { color: "bg-amber-100 text-amber-900 border-amber-300", icon: MinusCircle, label: "Leve" },
  D: { color: "bg-orange-100 text-orange-900 border-orange-300", icon: AlertTriangle, label: "Deficiente" },
  E: { color: "bg-red-100 text-red-900 border-red-300", icon: XCircle, label: "Eliminatoria" },
};

const IMG_A1A2 = "/assets/criterios-dgt-examen-moto-a1-a2.webp";
const IMG_AM = "/assets/criterios-dgt-examen-ciclomotor-am.webp";
const IMG_EB = "/assets/criterios-dgt-examen-remolque-be.webp";
const IMG_CAM = "/assets/criterios-dgt-examen-camion-c.webp";
const IMG_EC = "/assets/criterios-dgt-examen-trailer-ce.webp";
const IMG_D = "/assets/criterios-dgt-examen-autobus-d.webp";
const IMG_AML = "/assets/criterios-dgt-examen-cuadriciclo-aml.webp";
const IMG_B96 = "/assets/criterios-dgt-examen-b96.webp";

const DESC = {
  B_FRANJA: "Partiendo de la posición inicial, el aspirante realizará esta maniobra que consistirá en circular a velocidad reducida y uniforme, en primera relación de marcha, por una franja de anchura y longitud limitadas, sin salirse de ella ni perder el equilibrio. La franja estará delimitada a ambos lados por cualquier sistema que permita detectar la salida de la misma.",
  A_ZIGZAG: "El aspirante efectuará, en primera relación de marcha y a velocidad reducida, giros a derecha e izquierda alternativamente sorteando cinco jalones, sin salirse de la zona delimitada y sin arrollar, desplazar o derribar ningún jalón. La maniobra se iniciará por la derecha del primero, según gráfico.",
  E_ACEL: "Circulando, el aspirante aumentará progresivamente la velocidad, cambiando a segunda relación de marcha para alcanzar una velocidad de 30 km/h como mínimo. A continuación frenará con precisión dentro del espacio delimitado, pero sin llegar a rebasar la marca transversal de detención. La aceleración será ágil y sin tirones, los cambios sin rascados, manteniendo en todo momento el equilibrio. La detención será sin calar el motor.",
  C_CONOS: "El aspirante, partiendo de la posición de reposo, realizará los cambios de marcha necesarios en su caso, para alcanzar una velocidad mínima de 30 km/h; a continuación y sin reducir esta velocidad, describirá giros a derecha e izquierda alternativamente sorteando cinco conos, sin arrollar, desplazar o derribar ninguno. La maniobra se iniciará por la izquierda del primero, según gráfico.",
  D_OBST: "El aspirante realizará los cambios de marcha necesarios para alcanzar la velocidad de 50 km/h como mínimo en el primer paso señalizado y, sin reducir dicha velocidad, sorteará el obstáculo desplazándose hacia un lado para llevar de nuevo la motocicleta a la línea de marcha inicial, sin arrollar, desplazar o derribar ningún elemento de balizamiento.",
  F_FRENADO: "Circulando a 50 km/h el aspirante realizará una frenada de emergencia para detenerse dentro de la zona señalizada, sin rebasar la línea transversal de detención, manteniendo la trayectoria recta y sin perder el control del vehículo.",
  I_AML: "Rebasado el estacionamiento con la parte posterior del vehículo, paralelo al bordillo, se iniciará la maniobra de marcha atrás sin brusquedades, ni movimientos de la dirección con el vehículo inmovilizado, para situar el mismo dentro del espacio. Se permitirá un máximo de tres movimientos tanto en la entrada como en la salida del estacionamiento. El vehículo deberá quedar paralelo al bordillo a una distancia máxima de 0,30 m. No deberá subir al bordillo ni forzarlo, pisar las marcas que delimiten la anchura de la calzada o golpear o tocar las vallas que delimiten el espacio para estacionar.",
  H_AML: "En primer lugar, inmovilizar el conjunto para proceder a desacoplar y desenganchar el remolque/semirremolque, dejando el vehículo al lado, y circular hacia adelante para situarse a una distancia de 10 m como mínimo. En segundo lugar, efectuar el acoplamiento, enganche y conexión de los elementos de unión, retirar calzos y recoger los soportes de apoyo. Durante el acoplamiento se podrá descender una vez para efectuar comprobaciones y orientar la lanza, si es posible.",
};

const MOTO_SECTIONS = [
  {
    header: "Circuito de equilibrio y maniobras: enlazando las maniobras B, A y E, sin limitación de tiempo.",
    blocks: [
      { name: "Durante el desarrollo de la prueba" },
      { name: "B) Circular por una franja de anchura limitada", desc: DESC.B_FRANJA },
      { name: "A) Zig-zag entre jalones a velocidad reducida", desc: DESC.A_ZIGZAG },
      { name: "E) Aceleración y frenado", desc: DESC.E_ACEL },
    ],
  },
  {
    header: "Circuito de habilidades y velocidad: enlazando las maniobras C, D y F, con una limitación de tiempo de 25 segundos.",
    blocks: [
      { name: "C) Zig-zag entre conos", desc: DESC.C_CONOS },
      { name: "D) Evitar un obstáculo", desc: DESC.D_OBST },
      { name: "F) Frenado de emergencia controlado", desc: DESC.F_FRENADO },
    ],
  },
];

const DESTREZA_CONFIG = {
  A1: { img: { src: IMG_A1A2, title: "A1 y A2" }, sections: MOTO_SECTIONS },
  A2: { img: { src: IMG_A1A2, title: "A1 y A2" }, sections: MOTO_SECTIONS },
  AM: {
    img: { src: IMG_AM, title: "AM Ciclomotor" },
    sections: [{
      blocks: [
        { name: "Durante el desarrollo de la prueba" },
        { name: "B) Circular por una franja de anchura limitada", desc: DESC.B_FRANJA },
        { name: "A) Zig-zag entre jalones a velocidad reducida", desc: DESC.A_ZIGZAG },
      ],
    }],
  },
  AML: {
    img: { src: IMG_AML, title: "AML - Cuadriciclo Ligero" },
    sections: [{
      blocks: [
        { name: "I) Estacionamiento y salida de un espacio limitado", desc: DESC.I_AML },
        { name: "H) Cambio de sentido utilizando la marcha atrás", desc: DESC.H_AML },
      ],
    }],
  },
  BE: {
    img: { src: IMG_EB, title: "B+E - Remolques pesados" },
    sections: [{
      blocks: [
        { name: "Durante el desarrollo de la prueba" },
        { name: "G) Marcha atrás en recta y curva" },
        { name: "M) Acoplamiento y desacoplamiento del remolque" },
        { name: "N) Estacionamiento seguro para cargar o descargar (B+E)" },
      ],
    }],
  },
  C: { img: { src: IMG_CAM, title: "C - Camión" } },
  CE: { img: { src: IMG_EC, title: "C+E - Tráiler" } },
  D: { img: { src: IMG_D, title: "D - Autobús" } },
  B96: {
    img: { src: IMG_B96, title: "B96 - Conjunto hasta 4250Kg" },
    sections: [{
      blocks: [
        { name: "Durante el desarrollo de la prueba" },
        { name: "I) Estacionamiento y salida (línea, oblicuo o perpendicular)" },
        { name: "M) Acoplamiento y desacoplamiento del remolque" },
      ],
    }],
  },
};

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function ScoreCard({ tone, icon: Icon, title, text }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} />
        <div className="text-[11px] uppercase tracking-[0.2em] font-black">{title}</div>
      </div>
      <p className="text-sm">Se suspende con: {text}</p>
    </div>
  );
}

export default function CriteriosDgtPage() {
  const [permit, setPermit] = useState("B");
  const [test, setTest] = useState("C");
  const [gravedad, setGravedad] = useState("all");
  const [query, setQuery] = useState("");
  useSeo({
    title: "Criterios del Examen Práctico DGT | Autoescuelas Faro",
    description: "Todas las faltas del examen práctico de la DGT por permiso (B, moto, camión, autobús, remolques): leves, deficientes y eliminatorias, según criterio oficial.",
    canonical: `${SITE_URL}/criterios-dgt`,
    ogImage: "/assets/og/criterios-dgt-examen-camion-c.jpg",
    ogImageAlt: "Criterios de calificación del examen práctico de la DGT",
  });

  const handlePermitChange = (value) => {
    setPermit(value);
    if (NO_DESTREZA.has(value) && test === "D") setTest("C");
  };

  const availableTests = TESTS.filter((t) => !(NO_DESTREZA.has(permit) && t.id === "D"));

  const { sections, counts, totalGroups } = useMemo(() => {
    const q = norm(query.trim());
    const filtered = FALTAS.filter((f) => {
      if (f.ex !== test) return false;
      if (!f.p.includes(permit)) return false;
      if (gravedad !== "all" && f.g !== gravedad) return false;
      if (q && !norm(`${f.b} ${f.c} ${f.k} ${f.d} ${f.n}`).includes(q)) return false;
      return true;
    });
    const counts = { L: 0, D: 0, E: 0 };
    const map = new Map();
    for (const f of filtered) {
      counts[f.g] += 1;
      if (!map.has(f.b)) map.set(f.b, []);
      map.get(f.b).push(f);
    }
    const cfg = test === "D" ? DESTREZA_CONFIG[permit] : null;
    let sections;
    if (cfg?.sections) {
      const used = new Set();
      sections = cfg.sections.map((s) => ({
        header: s.header,
        groups: s.blocks
          .map((b) => {
            const faults = map.get(b.name);
            if (!faults) return null;
            used.add(b.name);
            return { name: b.name, desc: b.desc, faults };
          })
          .filter(Boolean),
      }));
      const leftover = [...map.entries()].filter(([n]) => !used.has(n)).map(([name, faults]) => ({ name, faults }));
      if (leftover.length) sections.push({ groups: leftover });
      sections = sections.filter((s) => s.groups.length > 0);
    } else {
      const groups = [...map.entries()].map(([name, faults]) => ({ name, faults }));
      sections = groups.length ? [{ groups }] : [];
    }
    const totalGroups = sections.reduce((n, s) => n + s.groups.length, 0);
    return { sections, counts, totalGroups };
  }, [permit, test, gravedad, query]);

  const permitLabel = PERMITS.find((p) => p.id === permit)?.label || permit;
  const currentTest = TESTS.find((t) => t.id === test);
  const totalFaults = counts.L + counts.D + counts.E;
  const permitHasTest = useMemo(() => FALTAS.some((f) => f.ex === test && f.p.includes(permit)), [permit, test]);
  const destrezaImg = test === "D" && totalFaults > 0 ? DESTREZA_CONFIG[permit]?.img : null;

  let groupCounter = 0;

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="criterios-dgt-page">
      <Header />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-blue)] hover:text-[var(--faro-red)]" data-testid="criterios-back">
          <ArrowLeft size={16} /> Volver al inicio
        </Link>
      </div>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Criterios oficiales DGT</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
          Selector de faltas del examen práctico
        </h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
          Las faltas oficiales de la hoja de calificación de la DGT, con su epígrafe, clave, calificación y las notas aclaratorias que maneja el examinador. Elige tu permiso y el tipo de prueba.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ScoreCard tone="bg-blue-50 border-blue-200 text-blue-900" icon={AlertTriangle} title="Destreza (circuito cerrado)" text={TESTS[0].baremo} />
          <ScoreCard tone="bg-indigo-50 border-indigo-200 text-indigo-900" icon={AlertTriangle} title="Circulación (vías abiertas)" text={TESTS[1].baremo} />
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Permiso</label>
              <select value={permit} onChange={(e) => handlePermitChange(e.target.value)} data-testid="permit-select" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)] bg-white">
                {PERMITS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-1">Tipo de prueba</label>
              <select value={test} onChange={(e) => setTest(e.target.value)} data-testid="test-select" className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)] bg-white">
                {availableTests.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-2 flex items-center gap-1"><Filter size={12} /> Filtrar por calificación</label>
              <div className="flex flex-wrap gap-2" data-testid="gravedad-filter">
                {[
                  { id: "all", label: "Todas" },
                  { id: "L", label: "Leve" },
                  { id: "D", label: "Deficiente" },
                  { id: "E", label: "Eliminatoria" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setGravedad(opt.id)}
                    data-testid={`gravedad-${opt.id}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider border transition-all ${
                      gravedad === opt.id
                        ? "bg-[var(--faro-blue-deep)] text-white border-[var(--faro-blue-deep)]"
                        : "bg-white text-[var(--faro-blue)] border-[rgba(30,42,143,0.2)] hover:border-[var(--faro-blue)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[var(--faro-blue)] mb-2 flex items-center gap-1"><Search size={12} /> Buscar</label>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ej.: stop, bordillo, intermitente..."
                data-testid="search-input"
                className="w-full rounded-xl border border-[rgba(30,42,143,0.15)] px-4 py-2.5 outline-none focus:border-[var(--faro-blue)] bg-white text-sm"
              />
            </div>
          </div>

          <div className="mt-4 text-sm text-[var(--faro-text-soft)]" data-testid="results-summary">
            Mostrando <b className="text-[var(--faro-blue-deep)]">{totalFaults} faltas</b> ({counts.L} leves · {counts.D} deficientes · {counts.E} eliminatorias) en <b className="text-[var(--faro-blue-deep)]">{totalGroups} epígrafes</b> para <b className="text-[var(--faro-blue-deep)]">{permitLabel}</b> · <b className="text-[var(--faro-blue-deep)]">{currentTest?.label}</b>.
          </div>
        </div>

        {destrezaImg && (
          <div className="mt-6 rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] overflow-hidden" data-testid="destreza-diagram">
            <div className="px-5 py-3 border-b border-[rgba(30,42,143,0.08)]">
              <div className="text-[10px] uppercase tracking-wider font-black text-[var(--faro-red)]">Circuito oficial</div>
              <div className="font-display font-black text-lg">{destrezaImg.title}</div>
            </div>
            <img src={destrezaImg.src} alt={`Circuito de destreza ${destrezaImg.title}`} className="w-full h-auto bg-white" loading="lazy" />
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="groups-list">
          {sections.length === 0 && (
            <div className="rounded-2xl bg-[var(--faro-bg)] p-6 text-center text-[var(--faro-text-soft)]" data-testid="empty-state">
              {!permitHasTest
                ? `Para el permiso ${permitLabel} la prueba se realiza íntegramente en ${test === "C" ? "circuito cerrado. Selecciona «Destreza» para ver sus faltas." : "vías abiertas. Selecciona «Circulación» para ver sus faltas."}`
                : "No hay faltas que coincidan con los filtros elegidos."}
            </div>
          )}
          {sections.map((section, sIdx) => (
            <React.Fragment key={section.header || section.groups[0]?.name || sIdx}>
              {section.header && (
                <div className="rounded-2xl bg-[var(--faro-blue-deep)] text-white px-5 py-4" data-testid={`section-header-${sIdx}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">Circuito</div>
                  <div className="font-display font-black text-base sm:text-lg mt-0.5 leading-snug">{section.header}</div>
                </div>
              )}
              {section.groups.map((g) => {
                groupCounter += 1;
                const idx = groupCounter;
                return (
                  <details key={g.name} open className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] overflow-hidden" data-testid={`group-${idx - 1}`}>
                    <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-3 hover:bg-[var(--faro-bg)]">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-9 h-9 rounded-full bg-[var(--faro-blue)] text-white flex items-center justify-center font-display font-black text-sm shrink-0">{idx}</span>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-black text-[var(--faro-red)]">{test === "D" ? "Maniobra" : "Epígrafe"}</div>
                          <div className="font-display font-black text-base sm:text-lg">{g.name}</div>
                        </div>
                      </div>
                      <span className="text-xs font-black text-[var(--faro-blue)] bg-[var(--faro-bg)] rounded-full px-3 py-1 shrink-0">{g.faults.length} falta{g.faults.length === 1 ? "" : "s"}</span>
                    </summary>
                    {g.desc && (
                      <p className="px-5 py-3 text-sm text-[var(--faro-text-soft)] bg-[var(--faro-bg)] border-t border-[rgba(30,42,143,0.08)]" data-testid={`group-desc-${idx - 1}`}>
                        {g.desc}
                      </p>
                    )}
                    <ul className="border-t border-[rgba(30,42,143,0.08)] divide-y divide-[rgba(30,42,143,0.06)]">
                      {g.faults.map((f, i) => {
                        const G = GRAVEDAD[f.g];
                        const Icon = G.icon;
                        return (
                          <li key={i} className="px-5 py-3 flex items-start gap-3">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider border rounded-full px-2 py-0.5 shrink-0 mt-0.5 ${G.color}`}>
                              <Icon size={11} /> {G.label}
                            </span>
                            <div className="min-w-0">
                              <div className="text-[11px] font-bold text-[var(--faro-blue)]">
                                <span className="font-mono">{f.c}</span>{f.k ? ` · ${f.k}` : ""}
                              </div>
                              <div className="text-sm mt-0.5">{f.d}</div>
                              {f.n && <div className="text-xs text-[var(--faro-text-soft)] italic mt-1">{f.n}</div>}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        <p className="mt-6 text-xs text-[var(--faro-text-soft)]">
          Transcripción de los documentos oficiales de la DGT: «Criterios de calificación» de circulación en vías abiertas (marzo 2026) y «Criterios de calificación de la prueba de control de aptitudes y comportamientos en circuito cerrado» (destreza).
        </p>

        <div className="mt-10 rounded-2xl bg-[var(--faro-blue)] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">¿Preparándote para el examen?</div>
            <div className="font-display font-black text-xl mt-1">Simulacros reales en Autoescuelas Faro</div>
            <p className="text-sm text-white/85 mt-1">Nuestros profesores conocen las rutas oficiales de la DGT en Vigo, Panxón, Nigrán y A Cañiza.</p>
          </div>
          <Link to="/#contacto" data-testid="criterios-cta" className="btn-pill btn-primary text-sm justify-center">
            Reservar clase <ArrowRight size={14} />
          </Link>
        </div>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
